/**
 * Shared Permission Table Component
 * Reusable table for displaying permissions in both Admin and Platform contexts
 */

import { type JSX } from 'react'
import { Column } from 'primereact/column'
import { KeyRound, Eye, Pencil, Trash2 } from 'lucide-react'
import DataTable from '../ui/DataTable.js'
import { formatDateTime } from '../../utils/formatDate.js'
import type { Permission } from './types.js'

interface PermissionTableProps {
  permissions: Record<string, Permission[]>
  loading: boolean
  onCreate: () => void
  onEdit: (permission: Permission) => void
  onDelete: (permission: Permission) => void
  onView?: (permission: Permission) => void
  createPermission?: string
  editPermission?: string
  deletePermission?: string
  canCreate?: boolean
  canEdit?: boolean
  canDelete?: boolean
}

export function PermissionTable({
  permissions,
  loading,
  onEdit,
  onDelete,
  onView,
  canEdit = true,
  canDelete = true,
}: PermissionTableProps): JSX.Element {
  const systemBodyTemplate = (rowData: Permission) => {
    return rowData.isSystem ? (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        System
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#3454cf]/5 text-[#3454cf]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#3454cf]" />
        Custom
      </span>
    )
  }

  const actionBodyTemplate = (rowData: Permission) => (
    <div className="flex items-center gap-1" style={{ flexWrap: 'nowrap' }}>
      {onView && (
        <button
          onClick={() => onView(rowData)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all shrink-0"
          title="View"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
      )}
      {canEdit && !rowData.isSystem && (
        <button
          onClick={() => onEdit(rowData)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all shrink-0"
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
      {canDelete && !rowData.isSystem && rowData.roleCount === 0 && (
        <button
          onClick={() => onDelete(rowData)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-red-500 transition-all shrink-0"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )

  const flatPermissions = Object.entries(permissions).flatMap(([domain, perms]) =>
    perms.map((p) => ({ ...p, domain }))
  )

  return (
    <DataTable
      value={flatPermissions}
      loading={loading}
      emptyMessage="No permissions found."
      className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
    >
      <Column
        field="key"
        header="Permission"
        body={(rowData: Permission) => (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
              <KeyRound className="h-5 w-5 text-[#3454cf]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 font-mono">{rowData.key}</p>
              <p className="text-xs text-gray-400">{rowData.description || '—'}</p>
            </div>
          </div>
        )}
        style={{ width: '30%' }}
      />
      <Column 
        field="domain" 
        header="Domain" 
        body={(rowData: Permission & { domain?: string }) => (
          <span className="text-xs font-semibold text-gray-500 uppercase">{rowData.domain || '—'}</span>
        )} 
      />
      <Column 
        field="roleCount" 
        header="Roles" 
        body={(rowData: Permission) => (
          <span className="text-sm font-medium text-gray-700">{rowData.roleCount ?? 0}</span>
        )} 
      />
      <Column body={systemBodyTemplate} header="Type" />
      <Column 
        field="createdAt" 
        header="Created At" 
        body={(rowData: Permission) => (
          <span className="text-sm font-medium text-gray-700">{formatDateTime(rowData.createdAt)}</span>
        )} 
      />
      <Column body={actionBodyTemplate} header="Actions" style={{ width: '140px' }} />
    </DataTable>
  )
}
